import React, { useState } from "react";
import { ButtonToolbar } from "react-bootstrap";
import { FaPen, FaTrashAlt } from "react-icons/fa";
import { graphql } from "react-apollo";
import { compose } from "recompose";

import Dialog from "../../components/dialog/dialog";
import CustomButton from "../../components/custom-button";
import {
  UpdateLeadActivityContainer,
  UpdateActivityContainer,
  UpdateHomeownerActivityContainer,
} from "../common-lead-prospect-forms/activity-container";
import Drawer from "../../hoc/Drawer";

import {
  get5WordsForTitle,
  getIconByEvent,
  formatDate,
} from "../../utils/helpers";
import { LEAD, PROSPECT, HOMEOWNER } from "../../utils/constants";

import {
  DELETE_LEAD_TRAFFIC,
  DELETE_PROSPECT_TRAFFIC,
  DELETE_HOMEOWNER_TRAFFIC,
} from "../../graphql/mutations";

/**
 * @module PastActivityTrafficListViewModule
 * this component is used to display the traffic activities in the past activities
 * @param {object} pastActivitiesData contains information related to the traffic activities
 * @param {string} type to check the type of the customer if its lead or prospect or homeowner
 * @param {function} onSubmit
 * @param {function} deleteLeadTraffic to delete lead type customer traffic
 * @param {function} deleteProspectTraffic to delete prospect type customer traffic
 * @param {function} deleteHomeownerTraffic to delete homeowner type customer traffic
 */
const PastActivityTrafficListView = (props) => {
  const {
    pastActivitiesData,
    deleteLeadTraffic,
    deleteProspectTraffic,
    deleteHomeownerTraffic,
    onSubmit,
    type,
    readOnly
  } = props;

  const [showConfirmationDialog, setshowConfirmationDialog] = useState(false);
  const [deleteId, setdeleteId] = useState();
  const [showEditModal, setShowEditModal] = useState(false);

  /**
   * this function displays the delete confirmation dialog
   * @function
   * @inner
   * @memberof module:PastActivityTrafficListViewModule
   * @param {object} item traffic item
   */
  const deleteHandler = (item) => {
    setshowConfirmationDialog(true);
    setdeleteId(item.id);
  };

  /**
   * this function hides the delete confirmation dialog
   * @function
   * @inner
   * @memberof module:PastActivityTrafficListViewModule
   */
  const cancelDelete = () => {
    setshowConfirmationDialog(false);
    setdeleteId();
  };

  /**
   * this function handle the delete confirmation and delete the traffic
   * @function
   * @inner
   * @memberof module:PastActivityTrafficListViewModule
   */
  const handleDelete = () => {
    if (type === PROSPECT) {
      deleteProspectTraffic({
        variables: {
          input: { traffic_ids: [deleteId] },
        },
      }).then((res) => {
        if (res.data.deleteProspectTraffic.code === 200) {
          onSubmit(res.data.deleteProspectTraffic.message);
          cancelDelete();
        }
      });
    } else if (type === LEAD) {
      deleteLeadTraffic({
        variables: {
          input: { traffic_ids: [deleteId] },
        },
      }).then((res) => {
        if (res.data.deleteLeadTraffic.code === 200) {
          onSubmit(res.data.deleteLeadTraffic.message);
          cancelDelete();
        }
      });
    } else if (type === HOMEOWNER) {
      deleteHomeownerTraffic({
        variables: {
          input: { traffic_ids: [deleteId] },
        },
      }).then((res) => {
        if (res.data.deleteHomeownerTraffic.code === 200) {
          onSubmit(res.data.deleteHomeownerTraffic.message);
          cancelDelete();
        }
      });
    }
  };

  /**
   * this function opens the edit modal
   * @function
   * @inner
   * @memberof module:PastActivityTrafficListViewModule
   */
  const handleEdit = () => {
    setShowEditModal(true);
  };

  /**
   * this function closes the edit drawer
   * @function
   * @inner
   * @memberof module:PastActivityTrafficListViewModule
   */
  const closeEditModal = () => {
    setShowEditModal(false);
  };

  /**
   * this function toggles the edit drawer
   * @function
   * @inner
   * @memberof module:PastActivityTrafficListViewModule
   */
  const toggleEditDrawer = () => {
    setShowEditModal(!showEditModal);
  };

  /**
   * this function handles submit after edit
   * @function
   * @inner
   * @memberof module:PastActivityTrafficListViewModule
   */
  const handleEditSubmit = (message) => {
    closeEditModal();
    onSubmit(message);
  };

  /**
   * this function renders the toggle part of the traffic activity in past activities
   * @function
   * @inner
   * @memberof module:PastActivityTrafficListViewModule
   * @param {object} item contains information related to the traffic activity
   * @returns {JSX.Element}
   */
  const toggle = (item) => {
    return (
      <div className="d-flex justify-content-between align-items-center">
        <div className="d-flex align-items-center">
          <div
            className="d-flex align-items-center"
            style={{ marginRight: "2px" }}
          >
            {getIconByEvent(pastActivitiesData.typeTitle)}
            {`${get5WordsForTitle(item.title)}: ${item.community?.name}`}
          </div>
        </div>
        <div className="d-flex align-items-center">
          <div className="toggle-date-time">
            {formatDate(item.created_at)}
          </div>
          {readOnly ? null : (
            <ButtonToolbar>
              <CustomButton
                size="sm"
                color="dark"
                btnIcon={<FaPen size={14} />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleEdit();
                }}
                className="icon-btn btn-icon"
              />
              <CustomButton
                size="sm"
                color="dark"
                btnIcon={<FaTrashAlt size={14} />}
                onClick={(e) => {
                  e.stopPropagation();
                  deleteHandler(item);
                }}
                className="icon-btn btn-icon"
              />
            </ButtonToolbar>
          )}
        </div>
      </div>
    );
  };

  /**
   * this function renders the collapse part of the traffic activity in past activities
   * @function
   * @inner
   * @memberof module:PastActivityTrafficListViewModule
   * @param {object} item contains information related to the traffic activity
   * @returns {JSX.Element}
   */
  const collapse = (item) => {
    return (
      <ul className="noted-descr-body">
        <li>
          <label>DETAILS:</label>
          <span>{item.details || '-'}</span>
        </li>
        <li>
          <label>SOURCE:</label>
          <span>{item.source || '-'}</span>
        </li>
      </ul>
    );
  };

  // Determine which edit container to use based on type
  const EditContainer =
    type === LEAD
      ? UpdateLeadActivityContainer
      : type === PROSPECT
      ? UpdateActivityContainer
      : UpdateHomeownerActivityContainer;

  return (
    <div className="notes-card">
      <Dialog
        show={showConfirmationDialog}
        onHide={cancelDelete}
        title="Delete Confirmation"
        body="Are you sure you want to delete?"
        click={handleDelete}
        clickname="YES"
        closename="NO"
      />
      <Drawer
        show={showEditModal}
        toggleSideBar={toggleEditDrawer}
      >
        <EditContainer
          item={{
            ...pastActivitiesData,
            isTraffic: true,
            traffic_date: pastActivitiesData.traffic_date || pastActivitiesData.created_at,
            title: pastActivitiesData.title || "Traffic",
            details: pastActivitiesData.details || "",
          }}
          id={
            pastActivitiesData.prospect_id ||
            pastActivitiesData.lead_id ||
            pastActivitiesData.homeowner_id
          }
          type={type}
          function="UPDATE"
          close={closeEditModal}
          onSubmit={handleEditSubmit}
          editForm={true}
        />
      </Drawer>
      {props.render(toggle(pastActivitiesData), collapse(pastActivitiesData))}
    </div>
  );
};

export default compose(
  graphql(DELETE_LEAD_TRAFFIC, { name: "deleteLeadTraffic" }),
  graphql(DELETE_PROSPECT_TRAFFIC, { name: "deleteProspectTraffic" }),
  graphql(DELETE_HOMEOWNER_TRAFFIC, { name: "deleteHomeownerTraffic" })
)(PastActivityTrafficListView);